import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Loading from 'components/Loading';
import useReactNativeMessage from '../../../hooks/useReactNativeMessage';

/**
 * MobilePaymentBase component.
 * Base component that wraps payment components with common mobile payment functionality.
 * Uses Function as Child Component pattern for better transparency and flexibility.
 *
 * @param {object} props - Base props and child function
 * @param {Function} props.children - Function that receives enhanced props
 * @param {object} props.errors - Error object from the payment process
 * @param {Function} props.clearErrors - Function to clear errors
 * @param {Function} props.getPurchase - Function to fetch purchase data
 * @param {boolean} props.fetchingPayment - Whether payment is currently being fetched
 * @param {object} props.purchase - Purchase object containing payment details
 * @param {Function} props.resetPaymentCardError - Function to reset payment card errors
 * @param {boolean} props.cardErrorOccured - Whether a card error has occurred
 * @param {string} props.token - Purchase token from URL
 * @returns {React.ReactElement} The wrapped payment component
 */
const MobilePaymentBase = ({ children, ...props }) => {
  const {
    errors,
    clearErrors,
    getPurchase,
    resetPurchase,
    fetchingPayment,
    purchase,
    resetPaymentCardError,
    cardErrorOccured,
    token,
  } = props;

  const { availablePayments = [], isUpdating, loaded, token: storedToken } = purchase;
  // The store may still hold a previous purchase (e.g. a prior mobile checkout in the same
  // WebView session). The payment form must never initialize against stale data, so we treat
  // "stored token !== route token" as not-ready and refetch below.
  const purchaseMatchesRoute = storedToken === token;

  const [isLoading, setIsLoading] = useState(
    isUpdating || fetchingPayment || !loaded || !purchaseMatchesRoute,
  );
  const isCreditCardAvailable = availablePayments.some((type) => type === 'credit_card');
  const RNMessage = useReactNativeMessage();

  // Message handling functionality
  const setupMessageListener = useCallback(
    (handler) => {
      RNMessage.listen(handler);
    },
    [RNMessage],
  );

  const sendHeightMessage = useCallback(() => {
    const height = document.querySelector('div.l-container')?.clientHeight;
    if (height) {
      RNMessage.post({ height });
    }
  }, [RNMessage]);

  const sendErrorMessage = useCallback(
    (error) => {
      RNMessage.post({ error });
    },
    [RNMessage],
  );

  // Update height when loading state changes
  useEffect(() => {
    if (!isLoading) {
      sendHeightMessage();
    }
    RNMessage.post({ loading: isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, sendHeightMessage]);

  // Update loading state when dependencies change. While the stored purchase does not match the
  // route token (refetch in flight), stay in the loading state so we never publish a terminal
  // `{ loading: false }` computed from the previous purchase's data.
  useEffect(() => {
    setIsLoading(isUpdating || fetchingPayment || !loaded || !purchaseMatchesRoute);
  }, [loaded, isUpdating, fetchingPayment, purchaseMatchesRoute]);

  // Refetch the purchase whenever the route token changes or the store holds a different
  // purchase. Resetting first clears the stored token so the reducer's ENT-389 token-mismatch
  // guard (reducers/purchase.js) does not discard the fresh RECEIVE_PURCHASE. A legitimate
  // same-token remount keeps the existing data (storedToken === token) and does not refetch.
  useEffect(() => {
    if (storedToken !== token) {
      // Only reset when the store holds a *different* purchase; on a fresh/empty store there is
      // nothing to clear, so skip the redundant reset and just fetch.
      if (storedToken && typeof resetPurchase === 'function') {
        resetPurchase();
      }
      getPurchase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Mount-only side effects: clear any lingering card error and reset the installments plan on
  // unmount.
  useEffect(() => {
    if (typeof resetPaymentCardError === 'function') {
      resetPaymentCardError();
    }

    return () => {
      if (props.resetInstallmentsPlan) {
        props.resetInstallmentsPlan();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify mobile app if credit card is not available — but only once the stored purchase matches
  // the route token. Otherwise a stale previous purchase (different token) without `credit_card`
  // would emit this terminal error for the wrong purchase before the current one is fetched.
  useEffect(() => {
    if (purchaseMatchesRoute && loaded && !isCreditCardAvailable) {
      sendErrorMessage('Credit card not available');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreditCardAvailable, loaded, purchaseMatchesRoute]);

  // Handle card errors
  useEffect(() => {
    if (cardErrorOccured) {
      sendErrorMessage('Credit card payment error');
    }
  }, [cardErrorOccured, sendErrorMessage]);

  // Handle general errors
  useEffect(() => {
    if (errors && errors.code !== 0) {
      sendErrorMessage(errors.message);
      clearErrors();
    }
  }, [clearErrors, errors, sendErrorMessage]);

  return (
    <div className="l-mobile-purchase">
      <div className="l-container">
        {purchaseMatchesRoute ? (
          children({
            ...props, // Pass all original props
            RNMessage,
            sendErrorMessage,
            setupMessageListener,
            sendHeightMessage,
            isLoading: isLoading || !loaded || !isCreditCardAvailable,
            isCreditCardAvailable,
          })
        ) : (
          // Stored purchase does not match the route token yet (refetch in flight). Never mount
          // the payment form against stale data — keep loading until the tokens match.
          <Loading />
        )}
      </div>
    </div>
  );
};

MobilePaymentBase.propTypes = {
  children: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  clearErrors: PropTypes.func.isRequired,
  getPurchase: PropTypes.func.isRequired,
  resetPurchase: PropTypes.func,
  purchase: PropTypes.shape({
    isUpdating: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    availablePayments: PropTypes.array,
    total: PropTypes.number.isRequired,
    token: PropTypes.string,
  }),
  token: PropTypes.string.isRequired, // Token como prop directa
  paymentId: PropTypes.number,
  fetchingPayment: PropTypes.bool.isRequired,
  resetInstallmentsPlan: PropTypes.func.isRequired,
  cardErrorOccured: PropTypes.bool,
  resetPaymentCardError: PropTypes.func,
};

export default MobilePaymentBase;
